import { z } from 'zod';
import { LookSchema, OutfitConfigSchema, type Look, type OutfitConfig } from './avatar';
import type { SecondOpinion, Verdict } from './judge';

// ─────────────────────────────────────────────────────────────────────────────
// Phases + timing
// ─────────────────────────────────────────────────────────────────────────────

export const SHOW_PHASES = ['lobby', 'dressing', 'runway', 'results', 'closed'] as const;
export type ShowPhase = (typeof SHOW_PHASES)[number];

export const WALK_PHASES = ['intro', 'walking', 'judging', 'scored'] as const;
export type WalkPhase = (typeof WALK_PHASES)[number];

/** Poses the walker can strike. Keys 1-4 map to strike/turn/wave/twirl. */
export const WALK_POSES = ['idle', 'strike', 'turn', 'wave', 'twirl'] as const;
export type WalkPose = (typeof WALK_POSES)[number];
export const POSE_KEYS: Record<'1' | '2' | '3' | '4', WalkPose> = { '1': 'strike', '2': 'turn', '3': 'wave', '4': 'twirl' };

export const DRESS_SECONDS_DEFAULT = 180;
export const DRESS_SECONDS_OPTIONS = [60, 120, 180, 300] as const;
/** Hard cap for a single runway walk (manual or auto). */
export const WALK_MAX_MS = 45_000;
/** How long the verdict card stays before the next entrant. */
export const SCORED_HOLD_MS = 8_000;
/** Silence after which manual control reverts to auto-walk. */
export const MANUAL_IDLE_MS = 1_500;
/** Max 'walk.input' rate from the walker. */
export const INPUT_HZ = 15;
/** End-of-runway pose hold during auto walk. */
export const POSE_HOLD_MS = 2_500;
/**
 * Seconds the server holds a dropped player's seat during dressing/runway
 * (ShowRoom.allowReconnection). The client sizes its retry window from this too.
 */
export const RECONNECT_SECONDS = 30;
export const MAX_SHOW_PLAYERS = 10;
export const MAX_CHAT_LENGTH = 280;

// ─────────────────────────────────────────────────────────────────────────────
// Themes
// ─────────────────────────────────────────────────────────────────────────────

export type ShowTheme = { id: string; title: string; emoji: string; blurb: string };

export const THEMES: readonly ShowTheme[] = [
  { id: 'met-gala-sparkle', title: 'Met Gala Sparkle', emoji: '✨', blurb: 'Maximum glamour. Sequins, gowns, drama on the steps.' },
  { id: 'beach-day', title: 'Beach Day', emoji: '🏖️', blurb: 'Sun, sand and breezy layers. Sandals encouraged.' },
  { id: 'neon-night', title: 'Neon Night', emoji: '🌃', blurb: 'Electric colours that glow under blacklight.' },
  { id: 'enchanted-forest', title: 'Enchanted Forest', emoji: '🧚', blurb: 'Mossy greens, fairy wings and woodland magic.' },
  { id: 'winter-wonderland', title: 'Winter Wonderland', emoji: '❄️', blurb: 'Cozy knits, icy whites and a hint of frost.' },
  { id: 'space-explorer', title: 'Space Explorer', emoji: '🚀', blurb: 'Silver, chrome and looks from the year 3000.' },
  { id: 'nineties-throwback', title: '90s Throwback', emoji: '📼', blurb: 'Chokers, plaid, denim on denim. Totally.' },
  { id: 'rainy-day-chic', title: 'Rainy Day Chic', emoji: '☔', blurb: 'Puddle-proof and stylish. Boots and layers.' },
  { id: 'royal-ball', title: 'Royal Ball', emoji: '👑', blurb: 'Crowns, ballgowns and palace-worthy poise.' },
  { id: 'skater-street', title: 'Skater Street', emoji: '🛹', blurb: 'Loose fits, sneakers and effortless cool.' },
  { id: 'garden-tea-party', title: 'Garden Tea Party', emoji: '🫖', blurb: 'Florals, pastels and a very good hat.' },
  { id: 'superhero-casual', title: 'Superhero Casual', emoji: '🦸', blurb: 'Off-duty hero. Cape optional, confidence required.' },
  { id: 'candy-land', title: 'Candy Land', emoji: '🍭', blurb: 'Sugar-sweet pastels and playful shapes.' },
  { id: 'back-to-school', title: 'Back to School', emoji: '🎒', blurb: 'First-day fits: preppy, fresh and comfy.' },
  { id: 'movie-premiere', title: 'Movie Premiere', emoji: '🎬', blurb: 'Red carpet ready. Cameras are flashing.' },
  { id: 'safari', title: 'Safari', emoji: '🦒', blurb: 'Khaki, earth tones and adventure-ready layers.' },
  { id: 'underwater', title: 'Underwater', emoji: '🐚', blurb: 'Shimmering blues, teals and mermaid vibes.' },
  { id: 'cozy-cabin', title: 'Cozy Cabin', emoji: '🪵', blurb: 'Flannel, sweaters and fireside warmth.' },
];

export function themeById(id: string): ShowTheme | undefined {
  return THEMES.find((t) => t.id === id);
}

// ─────────────────────────────────────────────────────────────────────────────
// Room options (matchmaking payloads)
// ─────────────────────────────────────────────────────────────────────────────

/** Options passed by the host when creating a 'show' room (client.create('show', opts)). */
export const ShowRoomCreateOptionsSchema = z.object({
  showId: z.string().min(1),
  theme: z.string().min(1).max(120),
  judgeId: z.string().min(1),
  dressSeconds: z.number().int().min(30).max(900).default(DRESS_SECONDS_DEFAULT),
});
export type ShowRoomCreateOptions = z.input<typeof ShowRoomCreateOptionsSchema>;

/** Options passed when joining a show room by id. Reserved for later use. */
export type ShowRoomJoinOptions = Record<string, never>;

// ─────────────────────────────────────────────────────────────────────────────
// Client → server messages
// ─────────────────────────────────────────────────────────────────────────────

export const ShowMsg = {
  lookUpdate: 'look.update',
  ready: 'ready',
  hostStart: 'host.start',
  hostBeginRunway: 'host.beginRunway',
  hostSkipTimer: 'host.skipTimer',
  walkTakeControl: 'walk.takeControl',
  walkInput: 'walk.input',
  walkRelease: 'walk.release',
  secondOpinion: 'secondOpinion',
  chat: 'chat',
  /** Host: skip the 'scored' hold and move to the next entrant (or results). */
  hostNext: 'host.next',
  /** Host: end the show now (any phase). Room goes to 'closed' and disposes. */
  hostClose: 'host.close',
} as const;
export type ShowMsgName = (typeof ShowMsg)[keyof typeof ShowMsg];

/**
 * Optional options a client may pass to joinById(roomId, opts) for a show room.
 * `outfit` pre-fills the entrant's look (e.g. their last saved outfit) so they do
 * not start dressing from an empty rack. Ignored for spectators.
 */
export const ShowRoomJoinOptionsSchema = z.object({ outfit: OutfitConfigSchema.optional() });
export type ShowRoomJoinOptionsInput = z.input<typeof ShowRoomJoinOptionsSchema>;

/** An outfit with nothing on the rack — what a fresh entrant starts dressing from. */
export const EMPTY_OUTFIT: OutfitConfig = { top: null, bottom: null, dress: null, shoes: null, accessories: [] };

export const LookUpdateMsgSchema = z.object({ look: LookSchema });
export const WalkInputMsgSchema = z.object({
  x: z.number().finite(),
  z: z.number().finite(),
  yaw: z.number().finite(),
  pose: z.enum(WALK_POSES),
});
export const SecondOpinionMsgSchema = z.object({ judgeId: z.string().min(1) });
export const ChatMsgSchema = z.object({ text: z.string().trim().min(1).max(MAX_CHAT_LENGTH) });
export const EmptyMsgSchema = z.object({}).passthrough();

export type ShowMsgPayloads = {
  'look.update': z.infer<typeof LookUpdateMsgSchema>;
  ready: Record<string, never>;
  'host.start': Record<string, never>;
  'host.beginRunway': Record<string, never>;
  'host.skipTimer': Record<string, never>;
  'walk.takeControl': Record<string, never>;
  'walk.input': z.infer<typeof WalkInputMsgSchema>;
  'walk.release': Record<string, never>;
  secondOpinion: z.infer<typeof SecondOpinionMsgSchema>;
  chat: z.infer<typeof ChatMsgSchema>;
  'host.next': Record<string, never>;
  'host.close': Record<string, never>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Server → client events
// ─────────────────────────────────────────────────────────────────────────────

export const ShowEvt = {
  toast: 'toast',
  chat: 'chat',
  verdict: 'verdict',
  error: 'error',
} as const;
export type ShowEvtName = (typeof ShowEvt)[keyof typeof ShowEvt];

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export type ShowEvtPayloads = {
  toast: { text: string; kind?: ToastKind };
  chat: { from: { userId: string; displayName: string }; text: string; at: number };
  /** kind 'score' = the official verdict (also mirrored into state.verdicts); 'second' = a second opinion (feedback only). */
  verdict: { userId: string; verdict: Verdict; kind?: 'score' | 'second' };
  error: { message: string; code?: string };
};

/** One row of show_entries as returned by GET /api/shows/:id. */
export type ShowEntry = {
  userId: string;
  displayName: string;
  picture: string | null;
  look: Look;
  walkOrder: number | null;
  score: number | null;
  verdict: Verdict | null;
  secondOpinions: SecondOpinion[];
  walkedAt: string | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Plain-object mirror of the room state for the web store
// ─────────────────────────────────────────────────────────────────────────────

export type PlayerSnapshot = {
  sessionId: string;
  userId: string;
  displayName: string;
  /** Parsed from the synced JSON string; null until the player has one. */
  look: Look | null;
  ready: boolean;
  connected: boolean;
};

export type WalkSnapshot = {
  entrantUserId: string | null;
  phase: WalkPhase;
  progress: number; // 0..1 along the runway path
  manual: boolean;
  x: number;
  z: number;
  yaw: number;
  pose: WalkPose;
  startedAt: number; // server ms
};

export type VerdictSnapshot = {
  score: number;
  headline: string;
  feedback: string;
  sub: Verdict['sub'];
  judgeId: string;
};

export type RankingEntry = { userId: string; displayName: string; score: number; walkOrder: number };

export type ShowSnapshot = {
  phase: ShowPhase;
  theme: string;
  judgeId: string;
  hostId: string;
  dressSeconds: number;
  /** Server ms when dressing ends; 0 when not dressing. */
  dressEndsAt: number;
  /** Keyed by Colyseus sessionId. */
  players: Record<string, PlayerSnapshot>;
  walk: WalkSnapshot;
  /** Keyed by userId. */
  verdicts: Record<string, VerdictSnapshot>;
  secondOpinions: SecondOpinion[];
  results: { winnerUserId: string | null; ranking: RankingEntry[] };
};

export const EMPTY_WALK: WalkSnapshot = {
  entrantUserId: null,
  phase: 'intro',
  progress: 0,
  manual: false,
  x: 0,
  z: 0,
  yaw: 0,
  pose: 'idle',
  startedAt: 0,
};

export const EMPTY_SHOW_SNAPSHOT: ShowSnapshot = {
  phase: 'lobby',
  theme: '',
  judgeId: '',
  hostId: '',
  dressSeconds: DRESS_SECONDS_DEFAULT,
  dressEndsAt: 0,
  players: {},
  walk: EMPTY_WALK,
  verdicts: {},
  secondOpinions: [],
  results: { winnerUserId: null, ranking: [] },
};

/** Safe parse of the synced look JSON string. */
export function parseLookJson(json: string): Look | null {
  if (!json) return null;
  try {
    const parsed = LookSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
